import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';

export default function IndustrialCostTransformationsView() {
  const [materialFilter, setMaterialFilter] = useState<'all' | string>('all');
  const [stageFilter, setStageFilter] = useState<'all' | string>('all');
  const [periodFilter, setPeriodFilter] = useState('2025');

  const rows: Array<{
    id: string;
    material: string;
    stage: string;
    date: string;
    grossInputQty: string;
    usefulOutputQty: string;
    yieldPct: string;
    usefulUnitCost: string;
    lotRef: string;
    qualityRef: string;
    sourceType: string;
    sourceRef: string;
  }> = [];

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Stack spacing={1.25}>
            <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Transformación
            </Typography>

            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              Base operativa de conversión entre entrada y salida útil. Aquí vivirán los eventos
              de transformación que permiten medir rendimiento, asociarlos a una etapa y formar el
              coste útil de la materia prima.
            </Typography>

            <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
              Esta vista no representa la compra ni el producto final. Representa la conversión
              industrial entre bruto y útil, con fecha real, etapa asociada y trazabilidad por
              bloque o lote cuando exista.
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                px: 2.25,
                py: 1.15,
                borderColor: 'rgba(56,189,248,0.4)',
                color: '#e2e8f0',
              }}
            >
              Importar transformación
            </Button>

            <Button
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                px: 2.25,
                py: 1.15,
                bgcolor: '#38bdf8',
                color: '#0f172a',
                '&:hover': {
                  bgcolor: '#38bdf8',
                  filter: 'brightness(1.06)',
                },
              }}
            >
              Nueva transformación
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: 3,
          py: 3,
        }}
      >
        <Stack spacing={2.25}>
          <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
            Filtros de transformación
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 1.5,
            }}
          >
            <TextField
              select
              label="Material"
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="all">Todos</MenuItem>
            </TextField>

            <TextField
              select
              label="Etapa"
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="all">Todas</MenuItem>
            </TextField>

            <TextField
              label="Periodo"
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>

          <Alert severity="info" variant="outlined">
            En esta primera fase la vista queda preparada para registrar eventos de transformación,
            pero todavía no está conectada al backend real ni al cálculo automático del coste útil.
          </Alert>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
          <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
            Eventos de transformación
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
            Conversión entre entrada bruta y salida útil, con rendimiento, etapa asociada y
            trazabilidad suficiente para formar el coste útil.
          </Typography>
        </Box>

        {rows.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <IndustrialCostSectionPlaceholder
              title="Sin transformaciones todavía"
              description="Aquí aparecerán los eventos de transformación entre bruto y útil. El siguiente paso será conectar esta vista con servicios reales y preparar el flujo operativo de rendimiento."
            />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Material</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Etapa</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Fecha / periodo</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                    Entrada bruta
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                    Salida útil
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                    Rendimiento
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                    Coste útil unitario
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Lote</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Calidad</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Origen</TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Referencia</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                      {row.material}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.stage}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.date}</TableCell>
                    <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                      {row.grossInputQty}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                      {row.usefulOutputQty}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                      {row.yieldPct}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                      {row.usefulUnitCost}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.lotRef}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.qualityRef}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.sourceType}</TableCell>
                    <TableCell sx={{ color: '#cbd5e1' }}>{row.sourceRef}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
    </Stack>
  );
}